/**
 * RedirectGate — silent component mounted inside the router that checks
 * every pathname change against the admin-managed url_redirects table.
 *
 * If `/the-naked-sketch` (or any other path) matches an active redirect,
 * the user is `navigate(target, { replace: true })`-ed within milliseconds.
 * No flash, no 404, no email-link regret. Hit-count is incremented
 * server-side on every successful match.
 *
 * Paths that should NEVER trigger a lookup (root, common SPA prefixes,
 * already-rendered app routes) are skipped to keep the gate cheap.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skip-list — prefixes that we KNOW are real app routes. Lookup never fires
// for these (no API roundtrip, no perf cost on hot paths).
const SKIP_PREFIXES = [
  '/api/',
  '/admin/',
  '/event/',
  '/events/',
  '/organization/',
  '/dashboard',
  '/login',
  '/signup',
  '/marketplace',
  '/vendors/',
  '/venues/',
  '/food-trucks',
  '/find-events',
  '/sell-tickets',
  '/blog/',
  '/profile',
  '/cart',
  '/checkout',
  '/booking',
  '/team',
  '/sales',
  '/m/',  // marketing short-codes
];

function shouldSkip(pathname) {
  if (!pathname || pathname === '/' || pathname.length < 2) return true;
  return SKIP_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export default function RedirectGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const inFlightRef = useRef(null);

  useEffect(() => {
    const path = location.pathname || '';
    if (shouldSkip(path)) return;
    if (inFlightRef.current === path) return;
    inFlightRef.current = path;

    (async () => {
      try {
        // Use fetch directly so the global axios interceptor stack doesn't
        // touch this lookup (interceptor swallows quiet 4xx replies in some
        // code paths).
        const r = await fetch(`${API}/redirects/lookup?path=${encodeURIComponent(path)}`);
        const data = r.ok ? await r.json() : null;
        if (data?.match && data.target) {
          // window.location.href guarantees the redirect wins even when the
          // matching SPA route already rendered on initial load, and works
          // for both internal paths and absolute URLs.
          window.location.href = data.target;
        }
      } catch (_) {
        // 404 / network — let the SPA render its own not-found state.
      } finally {
        if (inFlightRef.current === path) inFlightRef.current = null;
      }
    })();
  }, [location.pathname, navigate]);

  return null;
}
